import type { AxiosInstance } from 'axios'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { isErrorStatus } from '../utils'
import type { CustomApiError } from '../types'
import { filter } from '../../utils/api/filterBuilder'
import type { PromoterIntegrationTask } from './types'

const pendingStatuses = ['in-progress', 'incomplete', 'pending', 'started']
export const completedStatuses = ['completed', 'failed', 'cancelled']

dayjs.extend(utc)

async function fetchTasks(
  {
    axios,
    promoterOid,
    startedAfter,
    taskStatus,
    top = 15,
  }: {
    axios: AxiosInstance
    promoterOid: number
    startedAfter: string
    taskStatus: string[]
    top?: number
  },
): Promise<PromoterIntegrationTask[]> {
  const filterString = filter(
    { field: 'promoterOid', op: '=', value: promoterOid },
    { field: 'name', op: '!=', value: 'automation-block-message' },
    { field: 'status', op: 'in', value: taskStatus },
    {
      field: 'started',
      op: '>',
      value: dayjs(startedAfter).format('YYYY-MM-DDTHH:mm:ss[Z]'),
    },
  ) as string
  const { data, status } = await axios.get<PromoterIntegrationTask[]>(`/promoter/${promoterOid}/task`, {
    params: {
      $top: top,
      $filter: filterString,
      $orderby: 'started DESC',
      $select: 'oid,promoterOid,name,status,started,sysMtime,provider,messageListOid,messageTaskOid,campaignOid,eventOid,statusDetails,messageSubject,importFilename,importAssetType',
    },
  })
  if (isErrorStatus(status)) {
    throw {
      name: 'Get Promoter Task error',
      message: `Error getting Promoter Tasks for promoter ${promoterOid}.`,
      status,
    } as CustomApiError
  }
  // TODO - Remove the filter once backend has been updated
  return data.filter((item: any) =>
    !!startedAfter ? dayjs(item.started).isAfter(startedAfter) : true,
  )
}

export interface PromoterIntegrationTaskApi {
  fetchPending (promoterOid: number): Promise<PromoterIntegrationTask[]>

  fetchCompleted (promoterOid: number): Promise<PromoterIntegrationTask[]>
}

export default function (axios: AxiosInstance): PromoterIntegrationTaskApi {
  return {
    async fetchPending(promoterOid: number) {
      return fetchTasks({
        taskStatus: pendingStatuses,
        axios,
        promoterOid,
        startedAfter: dayjs()
          .utc()
          .subtract(12, 'hours')
          .format('YYYY-MM-DDTHH:mm:ss'),
      })
    },
    async fetchCompleted(promoterOid: number) {
      return fetchTasks({
        taskStatus: completedStatuses,
        axios,
        promoterOid,
        top: 5,
        startedAfter: dayjs()
          .utc()
          .subtract(1, 'day')
          .format('YYYY-MM-DD'),
      })
    },
  }
};
